// ─────────────────────────────────────────────────────────────────────────────
// Armazón del Programa de Reconocimiento.
//
// El módulo vive en su propia zona (/app/…) y monta sus propios proveedores y su hoja de
// maquetación (recognition-shell.css), pero el MENÚ LATERAL YA NO ES PROPIO: desde T-2 toda la
// aplicación usa `components/layout/Sidebar` en su variante `recognition`. Antes había dos menús
// distintos (este montaba `RecognitionSidebar`, un menú reducido con "Volver al portal"), y el
// prototipo tiene uno solo.
//
// Lo que cambia entre zonas lo resuelve el propio Sidebar por su prop `variant`: navegación SPA
// contra dura, clave de colapso, y campana contra selector de idioma en el pie.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminPanelProvider, useAdminPanelContext } from '@/contexts/AdminPanelContext';
import { UnsavedChangesProvider } from '@/contexts/UnsavedChangesContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { usePlatform } from '@/hooks/usePlatform';
import TeamsSDK from '@/teams/TeamsSDK';
import { GradientBackground } from '@/components/layout/GradientBackground';
import { HomeRightRail } from '@/components/layout/HomeRightRail';
import { LegalGate } from '@/views/recognition/access/LegalGate';
import { MockDataBadge } from '@/components/common/MockDataBadge';
import { PageSpinner } from '@/components/common/PageSpinner';
import { useBodyClass } from '@/hooks/useBodyClass';
import { useIsMobileViewport } from '@/hooks/useMediaQuery';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import { useFixedHomeEntryGuard } from '@/hooks/useFixedHomeEntryGuard';
import { hasFrontlineSession } from '@/auth/frontlineSession';
import { useIsSessionExpired } from '@/auth/sessionExpiredBus';
import { Sidebar } from '@/components/layout/Sidebar';
import { AdminFlyout } from '@/components/layout/Sidebar/AdminFlyout';
import { MobileNav } from '@/components/layout/MobileNav';
import { RecognitionDetailOverlay } from '@/views/recognition/RecognitionDetailOverlay';

import { BadgeUnlockModal } from '@/components/layout/BadgeUnlockModal';

import { PlatformBanner } from '@/components/layout/PlatformBanner';

import '@/views/recognition/recognition-shell.css';
import { withBase } from '@/utils/basePath';

// Rutas con barra inferior en móvil (personal operativo): el menú lateral se oculta y la
// navegación pasa a la tab bar, igual que en la maqueta.
//
// Pedido de negocio (2026-09-05) — `/app/admin` se agrega a esta lista para que el panel de
// administración también use la barra de abajo en móvil, en vez de que el `Sidebar` (`.side-nav`)
// se quede visible arriba convertido en fila horizontal (la única zona de Reconocimiento que
// todavía lo hacía). `reportes`/`equipo`/`tablero` se dejan fuera a propósito: son vistas
// secundarias sin ícono propio en NINGÚN nav (ni de escritorio), alcanzables hoy solo por link
// (Admin › Vistas / "Mi equipo" en el Perfil) — agregarles barra inferior sin un ícono que las
// represente no cambia nada para quien llega ahí.
//
// No hace falta ningún cambio en `Sidebar.tsx`: su efecto de deep-link (`isAdminPath` →
// `setIsAdminFlyoutOpen(true)`) y el `<MobileAdminFlyoutPortal/>` que abre el flyout a pantalla
// completa YA cubren `/app/admin*` — el `Sidebar` sigue montado siempre (solo el `.side-nav` se
// oculta por CSS), así que ese efecto corre igual aunque la barra de arriba esté invisible. Lo que
// SÍ cambió es `MobileNav.tsx`: el botón "Administración" de `RecognitionMobileNav` pasó de
// `NavLink` a botón-toggle (como ya hacía `PulsoMobileNav`) — con `NavLink`, reabrir el flyout tras
// cerrarlo sin cambiar de ruta no disparaba el efecto de deep-link (mismo `pathname`, sin cambio en
// sus deps) y el botón se quedaba sin poder reabrirlo.
const FRONTLINE_ROUTE_PREFIXES = [
  '/app/reconocer',
  '/app/brillamos',
  '/app/perfil',
  '/app/innovacion',
  '/app/nominar',
  '/app/admin',
];

const ADMIN_ROUTE_PREFIX = '/app/admin';

/**
 * Aplica el idioma del usuario a i18next dentro de esta zona. Va en un componente propio porque
 * `useAppLanguage` necesita estar bajo el `UserProvider` que monta este mismo armazón. Sin él, el
 * módulo de Reconocimiento ignoraba `demoUser.idioma` y se quedaba con el idioma del navegador.
 */
function LanguageSync() {
  useAppLanguage();
  return null;
}

/**
 * Segunda instancia de `AdminFlyout`, hermana de `.plataforma-shell` — fuera de la cadena de
 * `.side-nav`/`.side-nav-links` (BUG 2026-09-04: "se pierde la barra de navegación" en `/app/admin`
 * y cualquier ruta no-frontline en móvil). `.side-nav` necesita `overflow-x: auto` para el scroll
 * horizontal de sus iconos en el riel-convertido-en-fila (ver recognition-shell.css); por regla de
 * CSS ("si un eje no es `visible` el otro tampoco puede quedarse `visible`, se fuerza a `auto`"),
 * eso vuelve `overflow-y` efectivamente `auto` también — y un `overflow` distinto de `visible` en
 * un ancestro SÍ recorta a un descendiente `position: fixed`, aunque su tamaño/posición se calculen
 * contra el viewport. El AdminFlyout embebido de Sidebar.tsx (dentro de `.admin-link-wrap--bottom`,
 * a su vez dentro de `.side-nav-links`) quedaba así recortado a los ~56px visibles de `.side-nav`
 * en vez de ocupar la pantalla completa que su propio CSS pide (`position: fixed; inset: 0`,
 * Sidebar.css @media max-width:768px) — de ahí el hueco en blanco y la imposibilidad de alcanzar
 * las secciones del panel.
 *
 * Mismo patrón que YA usa Pulso para este mismo problema: una instancia hermana fuera de esa
 * cadena de overflow, envuelta en `.mobile-admin-flyout-wrap` (Sidebar.css) — la propia hoja ya la
 * oculta en escritorio (`display: none`) y la activa en móvil (`display: contents`), sin CSS
 * nuevo. Aparte de `AppLayout` (no dentro) porque necesita `useAdminPanelContext()`, y el
 * `AdminPanelProvider` recién se abre en el JSX que `AppLayout` devuelve.
 */
function MobileAdminFlyoutPortal() {
  const { isAdminFlyoutOpen } = useAdminPanelContext();
  return (
    <div className="mobile-admin-flyout-wrap">
      {isAdminFlyoutOpen && <AdminFlyout shouldCloseOnSelect />}
    </div>
  );
}

export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobileViewport();
  const isFrontlineRoute = FRONTLINE_ROUTE_PREFIXES.some(p => location.pathname.startsWith(p));
  const isFrontlineMobile = isMobile && isFrontlineRoute;
  const isAdminRoute = location.pathname.startsWith(ADMIN_ROUTE_PREFIX);

  const scenario = new URLSearchParams(location.search).get('scenario');

  useBodyClass('frontline-mobile-mode', isFrontlineMobile);
  useBodyClass('plataforma-admin-mode', isAdminRoute);

  // Un usuario SIN módulo Brillamos que aterriza en esta zona (`/app/...`) —p. ej. un Teams tab o
  // bookmark que apunta directo a `/app/reconocer`, saltándose LandingDecision— no debe ver
  // contenido de Reconocimiento: su lugar es el portal Pulso. Simétrico al `goToBrillamosPortal` de
  // PulsoLayout.tsx. Se excluye `/app/admin*` a propósito: un AdminGlobal puede administrar el
  // Programa de Reconocimiento aunque su perfil personal no tenga el módulo Brillamos habilitado.
  const { modules, profileResolved } = useVisibleNavLinks();
  // Con la sesión expirada `modules` es el perfil de emergencia "solo Pulso" (`DEFAULT_MODULES`/
  // `FALLBACK_MODULES`, no un perfil real) — sin este guard, cualquier expiración de sesión a mitad
  // de uso de Reconocimiento se leía como "usuario sin Brillamos" y expulsaba al portal Pulso sin
  // aviso (bug reportado 2026-08-31). Mientras la sesión esté caída no se navega con esos datos.
  const isSessionExpired = useIsSessionExpired();
  const goToPulsoPortal = !isAdminRoute && !hasFrontlineSession() && profileResolved
    && !isSessionExpired && modules.canViewPulso && !modules.canViewBrillamos;
  useEffect(() => {
    if (goToPulsoPortal) window.location.replace(withBase('/'));
  }, [goToPulsoPortal]);

  // Bug Sesión #98 (extendido) — /app/reconocer y /app/brillamos son rutas FIJAS: un bookmark o
  // el tab de Teams que apunta directo a una de las dos se saltaba por completo la decisión de
  // `LandingDecision` (que ya cubre /app y el comodín /app/*). Ver el hook para el detalle de por
  // qué solo corre una vez, al entrar, y nunca al navegar después por el sidebar.
  //
  // Va ANTES del `if (goToPulsoPortal) return null` de abajo, a propósito: los Hooks no pueden
  // llamarse condicionalmente, y `goToPulsoPortal` puede pasar de `false` a `true` entre renders
  // de este mismo montaje (cuando el perfil termina de resolver) — colocarlo después habría hecho
  // que este componente invocara un número distinto de Hooks según el render.
  const { isResolvingHome } = useFixedHomeEntryGuard();

  // Teams theme sync — igual que PulsoLayout.tsx. Sin esto, esta zona nunca sincroniza el tema
  // real de Teams: mientras se está en Brillamos (que fuerza oscuro con `useForceDarkMode`, ver
  // BrillamosView.tsx) no se nota, pero al navegar a cualquier otra vista de esta zona (p. ej.
  // Fundamentales) el forzado se retira y queda expuesto el tema por defecto ('light'), nunca el
  // de Teams — bug reportado 2026-09-09 ("se quita el modo oscuro al entrar a Fundamentales").
  const { teamsContext } = usePlatform();
  const { setFromTeams } = useThemeContext();
  useEffect(() => {
    if (teamsContext) {
      setFromTeams(teamsContext.theme);
      TeamsSDK.registerOnThemeChangeHandler(setFromTeams);
    }
  }, [teamsContext, setFromTeams]);

  if (goToPulsoPortal) return null;

  return (
    <AdminPanelProvider>
    <UnsavedChangesProvider>
      <LanguageSync />
      <GradientBackground />
      {/* Rail de documentos del borde derecho. El demo lo pinta en TODAS las rutas salvo
          login (`Layout.tsx` : 70-89); aquí la zona `/app` es justamente ese conjunto. Las
          secciones admin y el layout frontline móvil lo ocultan por CSS
          (`body.plataforma-admin-mode` / `body.frontline-mobile-mode`). */}
      <HomeRightRail />
      <MobileAdminFlyoutPortal />

      {/* Aceptación legal bloqueante: cubre solo las rutas del módulo. */}
      <LegalGate>
        {scenario === 'new-recognition' && (
          <PlatformBanner variant="success" onClose={() => {}}>
            {t('scenarios.newRecognition')}
          </PlatformBanner>
        )}
        {scenario === 'streak-risk' && (
          <PlatformBanner variant="warning" onClose={() => {}}>
            {t('scenarios.streakRisk')}
          </PlatformBanner>
        )}
        {scenario === 'campaign-ending' && (
          <PlatformBanner variant="info" onClose={() => {}}>
            {t('scenarios.campaignEnding')}
          </PlatformBanner>
        )}

        <div className={`plataforma-shell${isFrontlineMobile ? ' plataforma-shell--frontline' : ''}`}>
          <Sidebar variant="recognition" />

          {/* Solo `.plataforma-main` (recognition-shell.css): el modelo de layout de esta zona es
              flex row con el sidebar EN FLUJO (ver comentario "PARIDAD F1" en recognition-shell.css).
              `.main-content` (global.css) es del modelo del portal — sidebar `position:fixed` +
              `margin-left: var(--sidebar-width)` sobre el contenido. Combinar ambas clases sumaba los
              dos offsets (sidebar en flujo + margin-left del modelo fixed), dejando el contenido
              aplastado contra el borde derecho a anchos medios (Teams, ~800-1024px) y un hueco vacío
              enorme junto al menú a anchos grandes. */}
          <main className="plataforma-main">
            {/* Bug Sesión #98 (extendido, 2ª vuelta) — mientras `useFixedHomeEntryGuard` no sepa
                si /app/reconocer o /app/brillamos son el destino correcto para la campaña activa,
                NO se monta el `<Outlet/>` de la ruta pedida por la URL: montarlo aquí pintaría un
                instante la pantalla equivocada antes de que la redirección alcance a correr. */}
            {isResolvingHome ? <PageSpinner /> : <Outlet />}
          </main>

          {isFrontlineMobile && <MobileNav />}
        </div>

        {scenario === 'badge-unlocked' && (
          <BadgeUnlockModal
            icon="⭐"
            title={t('scenarios.badgeUnlocked.title')}
            description={t('scenarios.badgeUnlocked.desc')}
            ctaLabel={t('scenarios.badgeUnlocked.cta')}
            onClose={() => {}}
          />
        )}
      </LegalGate>

      {/* Overlay del detalle de un reconocimiento (deep-link `?reco=` de la campana / correo). Va
          FUERA de LegalGate y a nivel de zona para que abra sobre cualquier ruta /app/* sin
          desmontarla. Se pinta solo si hay `?reco` en la URL. */}
      <RecognitionDetailOverlay />

      <MockDataBadge />
    </UnsavedChangesProvider>
    </AdminPanelProvider>
  );
}

export default AppLayout;
